import React, { useMemo } from 'react';
import { Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { PayslipRow, QueryResponse } from '../../../services/pw9335Api';

const { Text } = Typography;

interface ResultTableProps {
  result: QueryResponse;
}

const formatCurrency = (v: number): string => {
  return (v ?? 0).toLocaleString();
};

const ResultTable: React.FC<ResultTableProps> = ({ result }) => {
  // Build dynamic columns based on item defs
  const columns = useMemo((): ColumnsType<PayslipRow & { _key: string }> => {
    const cols: ColumnsType<PayslipRow & { _key: string }> = [
      {
        title: '部門',
        key: 'dept',
        width: 100,
        fixed: 'left',
        render: (_: unknown, record: PayslipRow) =>
          `${(record.deptCode || '').trim()} ${(record.deptName || '').trim()}`,
      },
      {
        title: '員工',
        key: 'emp',
        width: 120,
        fixed: 'left',
        render: (_: unknown, record: PayslipRow) =>
          `${(record.empCode || '').trim()} ${(record.empName || '').trim()}`,
      },
    ];

    // Add item columns (salary additions)
    if (result.addItemDefs && result.addItemDefs.length > 0) {
      for (let i = 0; i < result.addItemDefs.length; i++) {
        const def = result.addItemDefs[i];
        const idx = i;
        cols.push({
          title: <Text style={{ fontSize: 11 }}>{def.name}</Text>,
          key: `add_${def.code}`,
          width: 80,
          align: 'right',
          render: (_: unknown, record: PayslipRow) =>
            formatCurrency(record.addItems?.[idx] ?? 0),
        });
      }
    }

    // Add total column
    cols.push({
      title: <Text strong style={{ color: '#1890ff', fontSize: 11 }}>加項合計</Text>,
      key: 'addTotal',
      width: 90,
      align: 'right',
      render: (_: unknown, record: PayslipRow) => (
        <Text strong style={{ color: '#1890ff' }}>{formatCurrency(record.addTotal)}</Text>
      ),
    });

    // Deduct item columns
    if (result.deductItemDefs && result.deductItemDefs.length > 0) {
      for (let i = 0; i < result.deductItemDefs.length; i++) {
        const def = result.deductItemDefs[i];
        const idx = i;
        cols.push({
          title: <Text style={{ fontSize: 11 }}>{def.name}</Text>,
          key: `deduct_${def.code}`,
          width: 80,
          align: 'right',
          render: (_: unknown, record: PayslipRow) =>
            formatCurrency(record.deductItems?.[idx] ?? 0),
        });
      }
    }

    // Deduct total, net pay, retire, health ins
    cols.push(
      {
        title: <Text strong style={{ color: '#ff4d4f', fontSize: 11 }}>減項合計</Text>,
        key: 'deductTotal',
        width: 90,
        align: 'right',
        render: (_: unknown, record: PayslipRow) => (
          <Text strong style={{ color: '#ff4d4f' }}>{formatCurrency(record.deductTotal)}</Text>
        ),
      },
      {
        title: <Text strong style={{ color: '#52c41a', fontSize: 11 }}>實發薪資</Text>,
        key: 'netPay',
        width: 100,
        align: 'right',
        render: (_: unknown, record: PayslipRow) => (
          <Text strong style={{ color: '#52c41a' }}>{formatCurrency(record.netPay)}</Text>
        ),
      },
      {
        title: <Text style={{ fontSize: 11 }}>勞退自提</Text>,
        key: 'retireSelf',
        width: 80,
        align: 'right',
        render: (_: unknown, record: PayslipRow) => formatCurrency(record.retireSelf),
      },
      {
        title: <Text style={{ fontSize: 11 }}>健保費用</Text>,
        key: 'healthIns',
        width: 80,
        align: 'right',
        render: (_: unknown, record: PayslipRow) => formatCurrency(record.healthIns),
      }
    );

    return cols;
  }, [result.addItemDefs, result.deductItemDefs]);

  const dataSource = useMemo(
    () =>
      (result.payslips || []).map((row, i) => ({
        ...row,
        _key: `${row.deptCode}-${row.empCode}-${i}`,
      })),
    [result.payslips]
  );

  const scrollX = useMemo(() => {
    const addCols = (result.addItemDefs?.length ?? 0) * 80;
    const deductCols = (result.deductItemDefs?.length ?? 0) * 80;
    return 220 + addCols + 90 + deductCols + 90 + 100 + 80 + 80;
  }, [result.addItemDefs, result.deductItemDefs]);

  return (
    <div>
      <div className="mb-2 text-sm text-gray-500">
        薪資年月: {result.yearMonth} | 共 {result.totalCount} 筆
      </div>
      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey="_key"
        size="small"
        scroll={{ x: scrollX, y: 500 }}
        pagination={{
          pageSize: 50,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
        }}
      />
      {result.thanksMessage && (
        <div className="mt-4 text-center text-gray-400 italic">
          {result.thanksMessage}
        </div>
      )}
    </div>
  );
};

export default ResultTable;
